import { Promise } from 'rsvp';
import Service, { inject as service } from '@ember/service';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { ERROR_MESSAGES } from '../../constants/error-messages';

/**
 * Service for payout-account related API calls
 */
export default Service.extend({
  api: service(),
  store: service(),
  errorTracker: service('error-tracker'),
  shData: service(),

  /**
   * Create a payoneer payout account for a user.
   *
   * @param {User} user
   * @param {String[]=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  createPayoneerPayoutAccountForUser(user, opt_includes = []) {
    const options = {};
    this.shData.addIncludesToOptions(options, opt_includes);

    return new Promise((resolve, reject) => {
      this.get('api').post(`users/${user.get('id')}/payout-accounts/payoneer-create`, options).then(response => {
        this.shData.pushResponseToStore(response);
        let payoutAccount = this.get('store').peekRecord('payoutAccount', response.data.id);
        resolve(payoutAccount);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          ERROR_MESSAGES.GENERIC_ERROR_MESSAGE,
          'createPayoneerPayoutAccountForUser -- error with user id: ' + user.get('id'),
        );
        reject(e);
      });
    });
  },

  createPayoutAccount({ user, accountType, accountMethod, status, providerId }) {
    const options = {
      jsonData: {
        account_type: accountType,
        account_method: accountMethod,
        status: status,
        provider_id: providerId,
      },
    };

    return this.get('api').post(`users/${user.get('id')}/payout-accounts`, options);
  },

  /**
   * Delete a payout account
   * @param payoutAccount
   * @return {Ember.RSVP.Promise}
   */
  deletePayoutAccount(payoutAccount) {
    let options = {};

    return this.get('api').delete(`payout-accounts/${payoutAccount.get('id')}`, options);
  },

  /**
   * Get all the payout accounts for the user.
   *
   * @param {User} user
   * @param {String[]=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  getPayoutAccountsForUser(user, opt_includes = []) {
    let options = {};
    this.shData.addIncludesToOptions(options, opt_includes);

    return new Promise((resolve, reject) => {
      this.get('api').request(`users/${user.get('id')}/payout-accounts/`, options).then(response => {
        this.shData.pushResponseToStore(response);
        let payoutAccounts = this.shData.getModelArrayFromResponse(response, 'payoutAccount');
        resolve(payoutAccounts);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error getting your payouts accounts. Please refresh and try again.',
          'getPayoutAccountsForUser -- error getting payout accounts user id: ' + user.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Mark all the activity feed items for a payout-account as viewed.
   *
   * @param {User} user
   * @return {Ember.RSVP.Promise}
   */
  markPayoutAccountViewed(user) {
    return new Promise((resolve, reject) => {
      const options = {};
      this.shData.addIncludesToOptions(options, []);

      this.get('api').post(`users/${user.get('id')}/payout-accounts/mark-viewed/`, options).then(() => {
        resolve(true);
      }).catch(e => {
        this.get('errorTracker').error(
          'markPayoutAccountViewed -- error marking viewed - app id: ' + user.get('id'),
          this,
          e);
        reject(e);
      });
    });
  },

  /**
   * Mark a PayoutAccount as a user's preferred account
   * @param {PayoutAccount|String} payoutAccount
   * @return {Ember.RSVP.Promise}
   */
  setPayoutAccountPreferred(payoutAccount) {
    const options = {};

    const payoutAccountId = payoutAccount.get ? payoutAccount.get('id') : payoutAccount;

    return this.get('api').post(
      `payout-accounts/${payoutAccountId}/set-preferred`,
      options,
    );
  },

  /**
   * Change a PayoutAccount's provider id
   * @param payoutAccount
   * @param newProviderId
   * @return {Ember.RSVP.Promise}
   */
  setPayoutAccountProviderId(payoutAccount, newProviderId) {
    const options = {
      jsonData: {
        'provider_id' : newProviderId,
      }
    };

    return this.get('api').post(`payout-accounts/${payoutAccount.get('id')}/set-provider-id`, options);
  },

  /**
   * Change a PayoutAccount's status
   * @param payoutAccount
   * @param newStatus
   * @return {Ember.RSVP.Promise}
   */
  setPayoutAccountStatus(payoutAccount, newStatus) {
    const options = {
      jsonData: {
        'status' : newStatus,
      }
    };

    return this.get('api').post(`payout-accounts/${payoutAccount.get('id')}/set-status`, options);
  },

  /**
   * Sync this user's payoneer account with the latest from Payoneer
   *
   * @param {User} user
   * @return {Ember.RSVP.Promise}
   */
  syncPayoneer(user) {
    const options = {};
    this.shData.addIncludesToOptions(options, []);

    return new Promise((resolve, reject) => {
      this.get('api').post(`users/${user.get('id')}/payout-accounts/sync`, options).then(response => {
        resolve(true);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          ERROR_MESSAGES.GENERIC_ERROR_MESSAGE,
          'syncPayoneer -- error syncing payoneer account for user id: ' + user.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * @param {PayoutAccount} payoutAccount
   * @param {Boolean} link
   * @return {Ember.RSVP.Promise}
   */
  setLinkedStatus(payoutAccount, link) {
    const endpoint = `payout-accounts/${payoutAccount.get('id')}/set-linked-status`;
    const options = { jsonData: { link } };
    return this.get('api').post(endpoint, options);
  },

  /**
   * @param {User} user
   * @return {Ember.RSVP.Promise}
  */
  syncPayoutAccounts(user) {
    const endpoint = `users/${user.get('id')}/payout-accounts/sync`;
    const options = {};
    return this.get('api').post(endpoint, options);
  },
});
